/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useMemo, useCallback } from 'react';
import {
  Input,
  Table,
  Tag,
  Empty,
  Spin,
  Drawer,
  Checkbox,
  Button,
  Badge,
  message,
  Select,
  Modal,
  Radio,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useQuery, useMutation, queryCache } from 'react-query';
import {
  loadSharedAdminTenders,
  postShareKeys,
} from '~src/services/api/shares';
import { useTenders } from '~src/hooks/api-hooks/useTenders';
import { appLog } from '~src/utils/log';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { getShareStatusVisualIntl } from '~src/utils/shareUtils/useShareKeysStatusUtils';
import type { DbShare } from '~src/types/db.d.ts';
import { getEvaluationOption } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersEvaluation/evaluationData';
import type { EvaluationValue } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersEvaluation/evaluationData';
import { getScreeningEvaluationOption } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersScreening/screeningDemoData';
import type { ScreeningEvaluationValue } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersScreening/screeningDemoData';
import { VendorFilterPanel } from './VendorFilterPanel';

// Helper functions for screening state
const getScreeningStateColor = (state: number): string => {
  const option = getScreeningEvaluationOption(
    state as ScreeningEvaluationValue
  );
  // Map colors from SCREENING_EVALUATION_OPTIONS to Ant Design color names
  return option.color === '#8c8c8c'
    ? 'default'
    : option.color === '#faad14' || option.color === '#fa8c16'
    ? 'orange'
    : option.color === '#52c41a'
    ? 'green'
    : option.color === '#ff4d4f'
    ? 'red'
    : 'default';
};

const getScreeningStateLabel = (state: number, intl: IntlShape): string => {
  const labelMap: Record<number, string> = {
    0: intl.formatMessage({
      id: 'import-tab-screening-undecided',
      defaultMessage: 'Undecided',
    }),
    1: intl.formatMessage({
      id: 'import-tab-screening-needs-attention',
      defaultMessage: 'Needs attention',
    }),
    2: intl.formatMessage({
      id: 'import-tab-screening-incomplete',
      defaultMessage: 'Incomplete',
    }),
    3: intl.formatMessage({
      id: 'import-tab-screening-accepted',
      defaultMessage: 'Accepted',
    }),
    4: intl.formatMessage({
      id: 'screening-row-rejected-option',
      defaultMessage: 'Rejected',
    }),
  };
  return (
    labelMap[state] ||
    intl.formatMessage({ id: 'unknown', defaultMessage: 'Unknown' })
  );
};

// Helper functions for evaluation state
const getEvaluationStateColor = (state: number): string => {
  const option = getEvaluationOption(state as EvaluationValue);
  // Map to Ant Design color names
  return option.color === '#8c8c8c'
    ? 'default'
    : option.color === '#1890ff'
    ? 'blue'
    : option.color === '#13c2c2'
    ? 'cyan'
    : option.color === '#52c41a'
    ? 'green'
    : option.color === '#ff4d4f'
    ? 'red'
    : option.color === '#7cb305'
    ? 'lime'
    : 'default';
};

const getEvaluationStateLabel = (state: number, intl: IntlShape): string => {
  const option = getEvaluationOption(state as EvaluationValue);
  return intl.formatMessage({
    id: option.intlId,
    defaultMessage: option.defaultLabel,
  });
};

interface ImportTabProps {
  projectId: number;
  tenderId: number;
  tenderCompleted: boolean;
}

interface VendorOffer {
  is_offer_created: boolean;
  offer_id: number | null;
  state: number | null;
  contract_id: number;
  contract_reference: string;
  screening_state: number | null;
  evaluation_state: number | null;
  is_accepted: boolean;
}

interface VendorFromTender {
  shareKeyId: string;
  email: string;
  title: string;
  screeningAccepted?: boolean;
  shareKeyState: number;
  shareKeyClaimedAt: number;
  shareKeyOpenedAt: number;
  shareKeyFirstInviteAt: number;
  isAlreadyInvited?: boolean;
  offers: VendorOffer[];
}

interface TenderWithVendors {
  tenderId: number;
  tenderTitle: string;
  tenderReference: string;
  vendors: VendorFromTender[];
  vendorCount: number;
}

interface SimpleTender {
  tenderId: number;
  tenderTitle: string;
  tenderReference: string;
}

export const ImportTab: React.FC<ImportTabProps> = ({
  projectId,
  tenderId,
  tenderCompleted,
}) => {
  const intl = useIntl();
  const t = (id: string, def: string) =>
    intl.formatMessage({ id, defaultMessage: def });
  const styles = useStyles(styleFn);

  const [searchText, setSearchText] = useState('');
  const [selectedTender, setSelectedTender] = useState<SimpleTender | null>(
    null
  );
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [drawerSearchText, setDrawerSearchText] = useState('');
  const [screeningFilter, setScreeningFilter] = useState<(number | null)[]>([
    0,
    3,
    4,
  ]);
  const [evaluationFilter, setEvaluationFilter] = useState<(number | null)[]>([
    0,
    1,
    2,
    3,
    4,
    5,
    null,
  ]);
  const [acceptedOffersFilter, setAcceptedOffersFilter] = useState<boolean>(
    false
  );
  const [vendorParticipationFilter, setVendorParticipationFilter] = useState<
    'mix' | 'delivered' | 'no-offers'
  >('mix');

  // Fetch all tenders in the project
  const tendersQuery = useTenders({ as_admin: true });

  // Fetch current tender's vendors to check for duplicates
  const { data: currentTenderVendors } = useQuery(
    ['current-tender-vendors', projectId, tenderId],
    async () => {
      const shareKeys = await loadSharedAdminTenders({
        projectId,
        tenderId: String(tenderId),
      });
      return shareKeys || [];
    },
    {
      enabled: !!projectId && !!tenderId,
    }
  );

  // Create a set of emails already invited to current tender
  const currentTenderEmails = useMemo(() => {
    if (!currentTenderVendors) return new Set<string>();
    return new Set(
      currentTenderVendors.map((v: any) =>
        (v.share_key_email || v.email || '').toLowerCase().trim()
      )
    );
  }, [currentTenderVendors]);

  // Lazy load vendors for selected tender only when drawer opens
  const { data: drawerVendors, isLoading: isLoadingDrawerVendors } = useQuery(
    ['drawer-tender-vendors', projectId, selectedTender?.tenderId],
    async () => {
      if (!selectedTender) return [];

      appLog('Fetching vendors for tender:', selectedTender.tenderId);

      try {
        const shareKeys = await loadSharedAdminTenders({
          projectId,
          tenderId: String(selectedTender.tenderId),
        });

        const vendors: VendorFromTender[] = (shareKeys || []).map(
          (share: any) => {
            const email = share.share_key_email || share.email || '';
            const title = share.share_key_title || share.title || '';
            const shareKeyId = share.share_key_id || share.id || '';
            const screeningAccepted =
              share.screening_accepted || share.is_accepted;
            const shareKeyState = share.share_key_state ?? 0;
            const shareKeyClaimedAt = share.share_key_claimed_at ?? 0;
            const shareKeyOpenedAt = share.share_key_opened_at ?? 0;
            const shareKeyFirstInviteAt = share.share_key_first_invite_at ?? 0;
            const offers: VendorOffer[] = (share.offers || []).map(
              (offer: any) => ({
                is_offer_created: offer.is_offer_created || false,
                offer_id: offer.offer_id || null,
                state: offer.state || null,
                contract_id: offer.contract_id,
                contract_reference: offer.contract_reference || '',
                screening_state: offer.screening_state ?? null,
                evaluation_state: offer.evaluation_state ?? null,
                is_accepted: offer.is_accepted || false,
              })
            );

            return {
              shareKeyId,
              email,
              title,
              screeningAccepted,
              shareKeyState,
              shareKeyClaimedAt,
              shareKeyOpenedAt,
              shareKeyFirstInviteAt,
              isAlreadyInvited: false, // Will be computed dynamically
              offers,
            };
          }
        );

        return vendors;
      } catch (error) {
        appLog(
          'Error loading vendors for tender:',
          selectedTender.tenderId,
          error
        );
        throw error;
      }
    },
    {
      enabled: !!projectId && !!selectedTender && drawerVisible,
      onError: (err) => {
        appLog('Error loading drawer vendors:', err);
        message.error(
          t(
            'import-drawer-error-vendors',
            'Failed to load vendors for this tender'
          )
        );
      },
    }
  );

  // Convert tenders list to simple format
  const simpleTenders = useMemo((): SimpleTender[] => {
    if (!tendersQuery.data) return [];

    // Filter out the current tender
    return tendersQuery.data
      .filter((tender) => String(tender.id) !== String(tenderId))
      .map((tender) => ({
        tenderId: Number(tender.id),
        tenderTitle:
          tender.title || t('import-tab-untitled-tender', 'Untitled Tender'),
        tenderReference: tender.reference || '',
      }));
  }, [tendersQuery.data, tenderId, t]);

  // Simple search filtering (only on tender title/reference)
  const filteredTenders = useMemo(() => {
    if (!simpleTenders) return [];
    if (!searchText.trim()) return simpleTenders;

    const searchLower = searchText.toLowerCase().trim();

    return simpleTenders.filter((tender) => {
      const matchesTender =
        tender.tenderTitle.toLowerCase().includes(searchLower) ||
        tender.tenderReference.toLowerCase().includes(searchLower);

      return matchesTender;
    });
  }, [simpleTenders, searchText]);

  // Filter vendors in drawer
  const filteredDrawerVendors = useMemo(() => {
    if (!drawerVendors || drawerVendors.length === 0) return [];

    // Return empty list if no filters are selected when in 'delivered' mode
    // Note: acceptedOffersFilter = false is the default (show all), so don't count it
    if (
      vendorParticipationFilter === 'delivered' &&
      screeningFilter.length === 0 &&
      evaluationFilter.length === 0
    ) {
      return [];
    }

    // Compute isAlreadyInvited dynamically for each vendor
    let vendorsWithInviteStatus = drawerVendors.map((vendor) => ({
      ...vendor,
      isAlreadyInvited: currentTenderEmails.has(
        vendor.email.toLowerCase().trim()
      ),
    }));

    // Filter by participation status (radio buttons)
    if (vendorParticipationFilter === 'delivered') {
      // Show only vendors who delivered offers
      vendorsWithInviteStatus = vendorsWithInviteStatus.filter((vendor) => {
        // Case 1: Empty offers array (tender has no contracts) - exclude
        if (vendor.offers.length === 0) return false;

        // Case 2: Check if at least one offer has actual participation
        // Show vendor if ANY of their offers has is_offer_created: true
        // Example: vendor with 2 contracts, participated in 1 but not the other -> SHOW
        // Only hide if ALL offers are is_offer_created: false
        return vendor.offers.some((offer) => offer.is_offer_created === true);
      });
    } else if (vendorParticipationFilter === 'no-offers') {
      // Show only vendors who didn't deliver offers
      vendorsWithInviteStatus = vendorsWithInviteStatus.filter((vendor) => {
        // Case 1: Empty offers array (tender has no contracts)
        if (vendor.offers.length === 0) return true;

        // Case 2: ALL offers have no actual participation (is_offer_created: false)
        // Example: vendor with 2 contracts, participated in 1 but not the other -> HIDE
        // Only show if ALL offers are is_offer_created: false
        return vendor.offers.every((offer) => offer.is_offer_created === false);
      });

      // Skip screening/evaluation filters when showing non-participating vendors
      // Filter by search text
      if (!drawerSearchText.trim()) return vendorsWithInviteStatus;

      const searchLower = drawerSearchText.toLowerCase().trim();
      return vendorsWithInviteStatus.filter(
        (vendor) =>
          vendor.email.toLowerCase().includes(searchLower) ||
          vendor.title.toLowerCase().includes(searchLower)
      );
    }
    // If 'mix', don't filter by participation - show all vendors

    // Only apply screening/evaluation filters when in 'delivered' mode
    if (vendorParticipationFilter === 'delivered') {
      // Filter by screening state (multi-select)
      // Treat null screening_state as 0 (UNDECIDED)
      if (screeningFilter.length > 0) {
        vendorsWithInviteStatus = vendorsWithInviteStatus.filter((vendor) => {
          return vendor.offers.some((offer) =>
            screeningFilter.includes(offer.screening_state ?? 0)
          );
        });
      }

      // Filter by evaluation state (multi-select)
      if (evaluationFilter.length > 0) {
        vendorsWithInviteStatus = vendorsWithInviteStatus.filter((vendor) => {
          return vendor.offers.some((offer) =>
            evaluationFilter.includes(offer.evaluation_state)
          );
        });
      }
    }

    // Filter by accepted offers toggle
    // When FALSE (default): Don't filter, show all vendors
    // When TRUE: Show only vendors WITH accepted offers
    if (acceptedOffersFilter) {
      vendorsWithInviteStatus = vendorsWithInviteStatus.filter((vendor) =>
        vendor.offers.some((offer) => offer.is_accepted === true)
      );
    }

    // Filter by search text
    if (!drawerSearchText.trim()) return vendorsWithInviteStatus;

    const searchLower = drawerSearchText.toLowerCase().trim();
    return vendorsWithInviteStatus.filter(
      (vendor) =>
        vendor.email.toLowerCase().includes(searchLower) ||
        vendor.title.toLowerCase().includes(searchLower) ||
        vendor.offers.some((offer) =>
          offer.contract_reference?.toLowerCase().includes(searchLower)
        )
    );
  }, [
    drawerVendors,
    drawerSearchText,
    currentTenderEmails,
    screeningFilter,
    evaluationFilter,
    acceptedOffersFilter,
    vendorParticipationFilter,
  ]);

  const handleTenderClick = useCallback((tender: SimpleTender) => {
    setSelectedTender(tender);
    setDrawerVisible(true);
    setSelectedVendors([]);
    setDrawerSearchText('');
    setScreeningFilter([0, 3, 4]);
    setEvaluationFilter([0, 1, 2, 3, 4, 5, null]);
    setAcceptedOffersFilter(false);
    setVendorParticipationFilter('mix');
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    setSelectedTender(null);
    setSelectedVendors([]);
    setDrawerSearchText('');
    setScreeningFilter([0, 3, 4]);
    setEvaluationFilter([0, 1, 2, 3, 4, 5, null]);
    setAcceptedOffersFilter(false);
    setVendorParticipationFilter('mix');
  }, []);

  const handleVendorToggle = useCallback((shareKeyId: string) => {
    setSelectedVendors((prev) =>
      prev.includes(shareKeyId)
        ? prev.filter((id) => id !== shareKeyId)
        : [...prev, shareKeyId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!selectedTender) return;
    const allIds = filteredDrawerVendors.map((v) => v.shareKeyId);
    setSelectedVendors(allIds);
  }, [selectedTender, filteredDrawerVendors]);

  const handleDeselectAll = useCallback(() => {
    setSelectedVendors([]);
  }, []);

  const handleClearFilters = useCallback(() => {
    setScreeningFilter([0, 3, 4]);
    setEvaluationFilter([0, 1, 2, 3, 4, 5, null]);
    setAcceptedOffersFilter(false);
    setVendorParticipationFilter('mix');
  }, []);

  // Import mutation
  const [doImportVendors, { isLoading: isImporting }] = useMutation(
    postShareKeys,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
        queryCache.invalidateQueries([
          'current-tender-vendors',
          projectId,
          tenderId,
        ]);
        message.success(
          t('import-vendors-success', 'Vendors imported successfully')
        );
        // Clear selected vendors after successful import
        setSelectedVendors([]);
      },
      onError: (error) => {
        appLog('import-vendors-error', error);
        message.error(t('import-vendors-error', 'Failed to import vendors'));
      },
    }
  );

  // Import handler with confirmation
  const handleImportVendors = async () => {
    if (!selectedTender || selectedVendors.length === 0) return;

    // Filter out already invited vendors
    const vendorsToImport = selectedVendors
      .map((shareKeyId) => {
        const vendor = drawerVendors?.find((v) => v.shareKeyId === shareKeyId);
        return vendor;
      })
      .filter((vendor) => {
        if (!vendor) return false;
        const isAlreadyInvited = currentTenderEmails.has(
          vendor.email.toLowerCase().trim()
        );
        return !isAlreadyInvited;
      });

    if (vendorsToImport.length === 0) {
      message.warning(
        t(
          'import-vendors-all-already-invited',
          'All selected vendors are already invited to this tender'
        )
      );
      return;
    }

    const skippedCount = selectedVendors.length - vendorsToImport.length;

    // Show confirmation modal
    Modal.confirm({
      title: intl.formatMessage({
        id: 'import-vendors-confirm-title',
        defaultMessage: 'Confirm Import',
      }),
      content: intl.formatMessage(
        {
          id: 'import-vendors-confirm-content',
          defaultMessage:
            'Are you sure you want to import {count} vendor(s) to this tender?',
        },
        { count: vendorsToImport.length }
      ),
      okText: intl.formatMessage({
        id: 'import-vendors-confirm-ok',
        defaultMessage: 'Import',
      }),
      cancelText: intl.formatMessage({
        id: 'cancel',
        defaultMessage: 'Cancel',
      }),
      onOk: async () => {
        const body = vendorsToImport.map((vendor) => ({
          email: vendor!.email,
          title: vendor!.title,
        }));

        if (skippedCount > 0) {
          message.info(
            intl.formatMessage(
              {
                id: 'import-vendors-skipped',
                defaultMessage: 'Skipping {count} already invited vendor(s)',
              },
              { count: skippedCount }
            )
          );
        }

        appLog('Importing vendors:', { projectId, tenderId, body });

        await doImportVendors({
          projectId,
          tenderId,
          body,
        } as any);
      },
    });
  };

  const tenderColumns: ColumnsType<SimpleTender> = [
    {
      title: t('import-tab-tender-title', 'Tender'),
      key: 'tender',
      render: (_, record) => (
        <div>
          <div
            css={{
              fontWeight: 600,
              fontSize: '15px',
              color: '#1f2937',
              marginBottom: 4,
            }}>
            {record.tenderTitle}
          </div>
          {record.tenderReference && (
            <div
              css={{
                fontSize: 13,
                color: '#4b5563',
                backgroundColor: '#f3f4f6',
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 500,
              }}>
              {record.tenderReference}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'center',
      render: () => (
        <div
          className="action-arrow"
          css={{ transition: 'transform 0.2s ease', color: '#9ca3af' }}>
          <FontAwesomeIcon
            icon={['fas', 'chevron-right']}
            css={{ fontSize: 14 }}
          />
        </div>
      ),
    },
  ];

  const isLoading = tendersQuery.isLoading;

  if (isLoading) {
    return (
      <div css={styles.section}>
        <div css={styles.loadingContainer}>
          <Spin size="large" />
          <div css={styles.loadingText}>
            {t('import-tab-loading', 'Loading tenders...')}
          </div>
        </div>
      </div>
    );
  }

  if (tendersQuery.error) {
    return (
      <div css={styles.section}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('import-tab-error', 'Failed to load tenders')}
        />
      </div>
    );
  }

  return (
    <div css={styles.section}>
      <div css={styles.sectionHeader}>
        <div css={styles.sectionTitle}>
          <FontAwesomeIcon
            icon={['fas', 'file-import']}
            css={{ color: '#2563eb' }}
          />
          <span>
            {t('import-tab-title', 'Import Vendors from Other Tenders')}
          </span>
        </div>
      </div>

      <div css={styles.searchContainer}>
        <Input
          size="large"
          placeholder={t(
            'import-tab-search-placeholder',
            'Search tenders by name or reference...'
          )}
          prefix={<FontAwesomeIcon icon={['fas', 'search']} />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      <div css={styles.tenderTableContainer}>
        <Table
          columns={tenderColumns}
          dataSource={filteredTenders}
          rowKey="tenderId"
          onRow={(record) => ({
            onClick: () => handleTenderClick(record),
            style: { cursor: 'pointer' },
          })}
          scroll={{ y: 275 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) =>
              `${total} ${t('import-tab-tenders', 'tender(s)')}`,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <div css={{ fontWeight: 500, color: '#4b5563' }}>
                      {t(
                        'import-tab-empty-tenders',
                        'No tenders with vendors found'
                      )}
                    </div>
                    <div css={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>
                      {t(
                        'import-tab-empty-tenders-desc',
                        'Tenders with available vendors will appear here'
                      )}
                    </div>
                  </div>
                }
              />
            ),
          }}
        />
      </div>

      <Drawer
        title={
          <div css={styles.drawerTitle}>
            <FontAwesomeIcon icon={['fas', 'users']} />
            {selectedTender
              ? `${t('import-tab-vendors-from', 'Available Vendors from')}: ${
                  selectedTender.tenderTitle
                }`
              : t('import-tab-vendors', 'Vendors')}
          </div>
        }
        width={640}
        visible={drawerVisible}
        onClose={handleDrawerClose}
        footer={
          !isLoadingDrawerVendors && (
            <div css={styles.drawerFooter}>
              <div css={styles.drawerFooterLeft}>
                {selectedVendors.length > 0 && (
                  <span css={styles.selectedCount}>
                    {selectedVendors.length}{' '}
                    {t('import-tab-selected', 'selected')}
                  </span>
                )}
              </div>
              <div css={styles.drawerFooterRight}>
                <Button onClick={handleDrawerClose} className="btn-cancel">
                  {t('import-tab-cancel', 'Cancel')}
                </Button>
                <Button
                  type="primary"
                  disabled={selectedVendors.length === 0}
                  loading={isImporting}
                  className="btn-submit"
                  onClick={handleImportVendors}>
                  {t('import-tab-import', 'Import Selected')} (
                  {selectedVendors.length})
                </Button>
              </div>
            </div>
          )
        }>
        <div css={styles.drawerContent}>
          {isLoadingDrawerVendors ? (
            <div css={styles.loadingContainer}>
              <Spin size="large" />
              <div css={styles.loadingText}>
                {t('import-drawer-loading-vendors', 'Loading vendors...')}
              </div>
            </div>
          ) : (
            <React.Fragment>
              <div css={styles.drawerSearchContainer}>
                <Input
                  placeholder={t(
                    'import-tab-drawer-search-placeholder',
                    'Search vendors in this tender...'
                  )}
                  prefix={<FontAwesomeIcon icon={['fas', 'search']} />}
                  value={drawerSearchText}
                  onChange={(e) => setDrawerSearchText(e.target.value)}
                  allowClear
                />
              </div>

              <VendorFilterPanel
                screeningFilter={screeningFilter}
                evaluationFilter={evaluationFilter}
                acceptedOffersFilter={acceptedOffersFilter}
                vendorParticipationFilter={vendorParticipationFilter}
                onScreeningChange={setScreeningFilter}
                onEvaluationChange={setEvaluationFilter}
                onAcceptedOffersChange={setAcceptedOffersFilter}
                onVendorParticipationChange={setVendorParticipationFilter}
              />

              <div css={styles.drawerActions}>
                <div css={styles.drawerActionsLeft}>
                  <Button size="small" onClick={handleSelectAll}>
                    {t('import-tab-select-all', 'Select All')}
                  </Button>
                  <Button size="small" onClick={handleDeselectAll}>
                    {t('import-tab-deselect-all', 'Deselect All')}
                  </Button>
                </div>
                <div css={styles.drawerActionsRight}>
                  <Button size="small" onClick={handleClearFilters}>
                    {t('import-tab-clear-filters', 'Clear Filters')}
                  </Button>
                </div>
              </div>

              <div css={styles.vendorList}>
                {filteredDrawerVendors.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('import-tab-no-vendors', 'No vendors found')}
                  />
                ) : (
                  filteredDrawerVendors.map((vendor) => (
                    <div
                      key={vendor.shareKeyId}
                      css={styles.vendorCard}
                      className={`${
                        selectedVendors.includes(vendor.shareKeyId)
                          ? 'selected'
                          : ''
                      } ${vendor.isAlreadyInvited ? 'disabled' : ''}`}
                      onClick={() =>
                        !vendor.isAlreadyInvited &&
                        handleVendorToggle(vendor.shareKeyId)
                      }>
                      <div
                        css={styles.vendorCardLeft}
                        onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedVendors.includes(vendor.shareKeyId)}
                          disabled={vendor.isAlreadyInvited}
                          onChange={() => handleVendorToggle(vendor.shareKeyId)}
                        />
                      </div>
                      <div css={styles.vendorCardContent}>
                        <div css={styles.vendorEmail}>{vendor.email}</div>
                        {vendor.title && (
                          <div css={styles.vendorTitle}>{vendor.title}</div>
                        )}
                        <div css={styles.vendorTags}>
                          {vendor.screeningAccepted !== undefined && (
                            <Tag
                              color={
                                vendor.screeningAccepted ? 'success' : 'default'
                              }
                              css={{ marginRight: 8 }}>
                              {vendor.screeningAccepted
                                ? t(
                                    'import-tab-screening-accepted',
                                    'Screening: Accepted'
                                  )
                                : t(
                                    'import-tab-screening-not-accepted',
                                    'Screening: Not Accepted'
                                  )}
                            </Tag>
                          )}
                          <Tag
                            color={
                              getShareStatusVisualIntl(intl, {
                                share_key_state: vendor.shareKeyState,
                                share_key_claimed_at: vendor.shareKeyClaimedAt,
                                share_key_opened_at: vendor.shareKeyOpenedAt,
                                share_key_first_invite_at:
                                  vendor.shareKeyFirstInviteAt,
                              } as DbShare).color
                            }>
                            {
                              getShareStatusVisualIntl(intl, {
                                share_key_state: vendor.shareKeyState,
                                share_key_claimed_at: vendor.shareKeyClaimedAt,
                                share_key_opened_at: vendor.shareKeyOpenedAt,
                                share_key_first_invite_at:
                                  vendor.shareKeyFirstInviteAt,
                              } as DbShare).text
                            }
                          </Tag>
                          {vendor.isAlreadyInvited && (
                            <Tag color="cyan" css={{ marginLeft: 8 }}>
                              {t(
                                'import-tab-already-invited',
                                'Already Invited'
                              )}
                            </Tag>
                          )}
                        </div>
                        {vendor.offers && vendor.offers.length > 0 && (
                          <div css={styles.vendorOffers}>
                            <div css={styles.offersLabel}>
                              {t('import-tab-contracts', 'Contracts:')}
                            </div>
                            <div css={styles.offersList}>
                              {vendor.offers.map((offer) => (
                                <div
                                  key={offer.contract_id}
                                  css={styles.offerItem}>
                                  <span css={styles.offerReference}>
                                    {offer.contract_reference}
                                  </span>
                                  {offer.screening_state !== null && (
                                    <Tag
                                      css={styles.offerTag}
                                      color={getScreeningStateColor(
                                        offer.screening_state
                                      )}>
                                      S:{' '}
                                      {getScreeningStateLabel(
                                        offer.screening_state,
                                        intl
                                      )}
                                    </Tag>
                                  )}
                                  {offer.screening_state !== 3 &&
                                    offer.is_accepted && (
                                      <Tag
                                        css={styles.offerTag}
                                        color="success"
                                        icon={
                                          <FontAwesomeIcon
                                            icon={['fas', 'check-circle']}
                                            style={{
                                              fontSize: 10,
                                              marginRight: 2,
                                            }}
                                          />
                                        }>
                                        {t(
                                          'import-tab-accepted-badge',
                                          'Accepted'
                                        )}
                                      </Tag>
                                    )}
                                  {offer.evaluation_state !== null && (
                                    <Tag
                                      css={styles.offerTag}
                                      color={getEvaluationStateColor(
                                        offer.evaluation_state
                                      )}>
                                      E:{' '}
                                      {getEvaluationStateLabel(
                                        offer.evaluation_state,
                                        intl
                                      )}
                                    </Tag>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </React.Fragment>
          )}
        </div>
      </Drawer>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: '8px 0',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 20,
    borderBottom: '1px solid #e5e7eb',
  },

  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    fontWeight: 700,
    fontSize: 22,
    color: '#111827',
    letterSpacing: '-0.025em',
  },

  searchContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    '& .ant-input-affix-wrapper-large': {
      borderRadius: 12,
      border: '1px solid #e5e7eb',
      background: '#ffffff',
      padding: '10px 16px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.02)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        borderColor: '#cbd5e1',
      },
      '&.ant-input-affix-wrapper-focused': {
        borderColor: '#2563eb',
        boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.1)',
      },
      '& .ant-input': {
        fontSize: 15,
        background: 'transparent',
        '&::placeholder': {
          color: '#94a3b8',
        },
      },
      '& .ant-input-prefix': {
        color: '#94a3b8',
        marginRight: 8,
        fontSize: 16,
      },
      '& .ant-input-suffix .ant-input-clear-icon': {
        color: '#94a3b8',
        '&:hover': { color: '#64748b' },
      },
    },
  },

  searchHint: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    background: '#eff6ff',
    color: '#1e40af',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    border: '1px solid #bfdbfe',
    '& svg': {
      marginRight: 10,
      fontSize: 15,
      color: '#3b82f6',
    },
  },

  tenderTableContainer: {
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
    '& .ant-table': {
      background: 'transparent',
    },
    '& .ant-table-thead > tr > th': {
      background: '#f9fafb',
      fontWeight: 600,
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
      padding: '14px 20px',
      '&::before': {
        display: 'none',
      },
    },
    '& .ant-table-tbody > tr': {
      transition: 'all 0.2s ease',
      '& > td': {
        padding: '18px 20px',
        borderBottom: '1px solid #f3f4f6',
      },
      '&:hover': {
        background: '#f8fafc',
        '& > td': {
          borderBottomColor: '#e2e8f0',
        },
        '& .action-arrow': {
          transform: 'translateX(4px)',
          color: '#2563eb',
        },
      },
      '&:last-child > td': {
        borderBottom: 'none',
      },
    },
    '& .ant-table-pagination': {
      padding: '16px 20px',
      margin: 0,
      borderTop: '1px solid #e5e7eb',
    },
  },

  drawerTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.02em',
    '& svg': {
      marginRight: 12,
      fontSize: 18,
      color: '#2563eb',
    },
  },

  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    height: '100%',
  },

  drawerSearchContainer: {
    paddingBottom: 2,
    '& .ant-input-affix-wrapper': {
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      background: '#ffffff',
      padding: '8px 12px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.02)',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: '#cbd5e1',
      },
      '&.ant-input-affix-wrapper-focused': {
        borderColor: '#2563eb',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
      },
      '& .ant-input': {
        fontSize: 14,
        background: 'transparent',
      },
      '& .ant-input-prefix': {
        color: '#94a3b8',
        marginRight: 6,
      },
    },
  },

  drawerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 14,
    borderBottom: '1px solid #f3f4f6',
    '& .ant-btn': {
      borderRadius: 6,
      fontWeight: 500,
      fontSize: 13,
      height: 30,
      padding: '0 12px',
      border: '1px solid #d1d5db',
      color: '#4b5563',
      background: '#ffffff',
      transition: 'all 0.15s ease',
      '&:hover': {
        borderColor: '#2563eb',
        color: '#2563eb',
      },
    },
  },

  drawerActionsLeft: {
    display: 'flex',
    gap: 8,
  },

  drawerActionsRight: {
    display: 'flex',
    gap: 8,
  },

  vendorList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxHeight: 'calc(100vh - 290px)',
    overflowY: 'auto',
    paddingRight: 4,
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f5f9',
      borderRadius: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#cbd5e1',
      borderRadius: 8,
      '&:hover': {
        background: '#94a3b8',
      },
    },
  },

  vendorCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
    '&:first-child': {
      marginTop: 12,
    },
    '&:hover': {
      borderColor: '#2563eb',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.05)',
      transform: 'translateY(-1px)',
    },
    '&.selected': {
      borderColor: '#2563eb',
      background: '#f8fafc',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.03)',
    },
    '&.disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      background: '#f9fafb',
      '&:hover': {
        borderColor: '#e5e7eb',
        boxShadow: 'none',
        transform: 'none',
      },
    },
  },

  vendorCardLeft: {
    paddingTop: 2,
    '& .ant-checkbox-wrapper': {
      '& .ant-checkbox': {
        '& .ant-checkbox-inner': {
          width: 19,
          height: 19,
          borderRadius: 5,
          border: '2px solid #cbd5e1',
          transition: 'all 0.15s ease',
        },
        '&.ant-checkbox-checked .ant-checkbox-inner': {
          background: '#2563eb',
          borderColor: '#2563eb',
        },
      },
    },
  },

  vendorCardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },

  vendorEmail: {
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
    lineHeight: 1.4,
  },

  vendorTitle: {
    fontSize: 13,
    color: '#4b5563',
    lineHeight: 1.4,
    fontWeight: 400,
  },

  vendorTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
    '& .ant-tag': {
      margin: 0,
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      padding: '3px 10px',
      border: '1px solid transparent',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
    },
    '& .ant-tag-success': {
      background: '#dcfce7',
      color: '#15803d',
    },
    '& .ant-tag-default': {
      background: '#f1f5f9',
      color: '#475569',
    },
    '& .ant-tag-blue': {
      background: '#e0f2fe',
      color: '#0369a1',
    },
    '& .ant-tag-orange': {
      background: '#ffedd5',
      color: '#c2410c',
    },
    '& .ant-tag-green': {
      background: '#dcfce7',
      color: '#15803d',
    },
    '& .ant-tag-red': {
      background: '#fee2e2',
      color: '#b91c1c',
    },
    '& .ant-tag-gold': {
      background: '#fef9c3',
      color: '#a16207',
    },
  },

  vendorOffers: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #e5e7eb',
  },

  offersLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 6,
  },

  offersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },

  offerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },

  offerReference: {
    fontSize: 12,
    fontWeight: 500,
    color: '#374151',
    backgroundColor: '#f9fafb',
    padding: '2px 6px',
    borderRadius: 4,
  },

  offerTag: {
    margin: 0,
    fontSize: 10,
    padding: '0 6px',
    lineHeight: '18px',
    fontWeight: 600,
  },

  drawerFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
  },

  drawerFooterLeft: {
    flex: 1,
  },

  drawerFooterRight: {
    display: 'flex',
    gap: 12,
    '& .ant-btn': {
      borderRadius: 8,
      fontWeight: 600,
      height: 40,
      padding: '0 20px',
      fontSize: 14,
      transition: 'all 0.2s ease',
    },
    '& .btn-cancel': {
      border: '1px solid #d1d5db',
      color: '#374151',
      '&:hover': {
        borderColor: '#9ca3af',
        color: '#111827',
      },
    },
    '& .btn-submit': {
      boxShadow:
        '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      '&:hover': {
        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
        transform: 'translateY(-0.5px)',
      },
      '&:disabled': {
        boxShadow: 'none',
        transform: 'none',
        background: '#f3f4f6',
        borderColor: '#e5e7eb',
        color: '#9ca3af',
      },
    },
  },

  selectedCount: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: '#2563eb',
    background: '#eff6ff',
    padding: '6px 14px',
    borderRadius: 8,
    border: '1px solid #bfdbfe',
  },

  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '100px 20px',
    gap: 16,
  },

  loadingText: {
    fontSize: 15,
    color: '#6b7280',
    fontWeight: 500,
  },
});
